/**
 * Environment Configuration and Validation
 * 
 * This module validates required environment variables at application startup
 * and provides typed access to configuration values.
 * 
 * Requirements: 2.1, 2.2, 7.1, 7.2, 7.3, 7.4, 7.5
 */

interface EnvConfig {
  // API Configuration
  apiBaseUrl: string;
  enableMocks: boolean;
  logLevel: 'debug' | 'info' | 'warn' | 'error';
  
  // Cognito Configuration
  cognito: {
    userPoolId: string;
    clientId: string;
    clientSecret?: string;
    domain: string;
    region: string;
  };
  
  // Redirect URIs
  redirectUris: {
    appCallback: string;
    appLogout: string;
  };
  
  // AWS Configuration
  aws: {
    s3Bucket: string;
    region: string;
  };
  
  // Application Configuration
  app: {
    name: string;
    version: string;
    skipAuth: boolean;
  };

  // DynamoDB Tables
  tables: {
    contracts: string;
    users: string;
    notifications: string;
    files: string;
    agents: string;
    tenants: string;
  };
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, defaultValue?: string): string {
  const value = import.meta.env[key];
  if (value !== undefined && value !== '') {
    return value;
  }
  if (defaultValue !== undefined) {
    return defaultValue;
  }
  throw new Error(`Missing required environment variable: ${key}`);
}

/**
 * Get boolean environment variable
 */
function getBooleanEnvVar(key: string, defaultValue = false): boolean {
  const value = import.meta.env[key];
  if (value === undefined || value === '') {
    return defaultValue;
  }
  return value === 'true' || value === '1';
}

/**
 * Validate required environment variables
 * 
 * Validates: Requirements 7.1, 7.2, 7.3, 7.4, 7.5
 */
export function validateEnv(): void {
  const requiredVars = [
    'VITE_COGNITO_USER_POOL_ID',
    'VITE_COGNITO_CLIENT_ID',
    'VITE_COGNITO_DOMAIN',
    'VITE_COGNITO_REGION',
  ];
  
  const missing: string[] = [];
  const warnings: string[] = [];
  
  // Check required variables
  for (const varName of requiredVars) {
    const value = import.meta.env[varName];
    if (!value || value === '') {
      missing.push(varName);
    }
  }
  
  // Check API Base URL (optional with default)
  if (!import.meta.env.VITE_API_BASE_URL) {
    warnings.push('VITE_API_BASE_URL not set, using default: http://localhost:3001/api/v1');
  }
  
  // Log validation results
  if (missing.length > 0) {
    console.error('[Config] ❌ Missing required environment variables:', missing);
    console.error('[Config] Please check your .env file and ensure all required variables are set.');
    throw new Error(`Missing required environment variables: ${missing.join(', ')}`);
  }
  
  if (warnings.length > 0) {
    warnings.forEach(warning => console.warn(`[Config] ⚠️  ${warning}`));
  }
  
  console.log('[Config] ✅ Environment validation passed');
}

/**
 * Log configuration status (non-sensitive information only)
 * 
 * Validates: Requirements 2.1, 2.2, 7.5
 */
export function logConfigStatus(): void {
  const config = getEnvConfig();
  
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log('🚀 Application Configuration');
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  console.log(`📦 App Name:        ${config.app.name} v${config.app.version}`);
  console.log(`🌐 API Base URL:    ${config.apiBaseUrl}`);
  console.log(`🎭 Mock Mode:       ${config.enableMocks ? '✅ Enabled' : '❌ Disabled'}`);
  console.log(`📝 Log Level:       ${config.logLevel.toUpperCase()}`);
  console.log(`🔐 Skip Auth:       ${config.app.skipAuth ? '✅ Yes' : '❌ No'}`);
  console.log(`🏢 Cognito Region:  ${config.cognito.region}`);
  console.log(`🪣 S3 Bucket:       ${config.aws.s3Bucket}`);
  console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
  
  // Additional warnings for development
  if (config.enableMocks) {
    console.warn('[Config] ⚠️  Running in MOCK mode - API calls will return static data');
  }
  
  if (config.app.skipAuth) {
    console.warn('[Config] ⚠️  Authentication is DISABLED - for development only!');
  }
  
  if (config.apiBaseUrl.includes('localhost')) {
    console.info('[Config] ℹ️  Using local API endpoint');
  }
}

/**
 * Get typed environment configuration
 * 
 * Validates: Requirements 2.1, 2.2
 */
export function getEnvConfig(): EnvConfig {
  return {
    // API Configuration
    apiBaseUrl: getEnvVar('VITE_API_BASE_URL', 'http://localhost:3001/api/v1'),
    enableMocks: getBooleanEnvVar('VITE_ENABLE_MOCKS', false),
    logLevel: (getEnvVar('VITE_LOG_LEVEL', 'debug') as EnvConfig['logLevel']),
    
    // Cognito Configuration
    cognito: {
      userPoolId: getEnvVar('VITE_COGNITO_USER_POOL_ID'),
      clientId: getEnvVar('VITE_COGNITO_CLIENT_ID'),
      clientSecret: import.meta.env.VITE_COGNITO_CLIENT_SECRET,
      domain: getEnvVar('VITE_COGNITO_DOMAIN'),
      region: getEnvVar('VITE_COGNITO_REGION'),
    },
    
    // Redirect URIs
    redirectUris: {
      appCallback: getEnvVar('VITE_APP_CALLBACK_URL', `${window.location.origin}/callback`),
      appLogout: getEnvVar('VITE_APP_LOGOUT_URL', window.location.origin),
    },
    
    // AWS Configuration
    aws: {
      s3Bucket: getEnvVar('VITE_S3_BUCKET', 'jai-be-safe-dev-documents'),
      region: getEnvVar('VITE_AWS_REGION', 'us-east-1'),
    },
    
    // Application Configuration
    app: {
      name: getEnvVar('VITE_APP_NAME', 'BeSafe'),
      version: getEnvVar('VITE_APP_VERSION', '0.1.0'),
      skipAuth: getBooleanEnvVar('VITE_SKIP_AUTH', false),
    },

    // DynamoDB Tables
    tables: {
      contracts: getEnvVar('VITE_CONTRACTS_TABLE', 'jai-be-safe-dev-contracts'),
      users: getEnvVar('VITE_USERS_TABLE', 'jai-be-safe-dev-users'),
      notifications: getEnvVar('VITE_NOTIFICATIONS_TABLE', 'jai-be-safe-dev-notifications'),
      files: getEnvVar('VITE_FILES_TABLE', 'jai-be-safe-dev-files'),
      agents: getEnvVar('VITE_AGENTS_TABLE', 'jai-be-safe-dev-agents'),
      tenants: getEnvVar('VITE_TENANTS_TABLE', 'jai-be-safe-dev-tenants'),
    },
  };
}

/**
 * Initialize environment configuration
 * 
 * Call this function at application startup to validate environment
 * and log configuration status.
 * 
 * Validates: Requirements 7.1, 7.2, 7.3, 7.4, 7.5
 */
export function initializeEnv(): EnvConfig {
  try {
    validateEnv();
    logConfigStatus();
    return getEnvConfig();
  } catch (error) {
    console.error('[Config] Failed to initialize environment:', error);
    throw error;
  }
}

// Export singleton config instance
export const env = getEnvConfig();
